import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/*
 * Remembers where a page was scrolled to, and returns there when the visitor
 * comes back with the browser's Back/Forward buttons. Any other visit to the
 * page starts at the top, including reloading the page.
 *
 * Used instead of React Router's <ScrollRestoration> because the site turns on
 * smooth scrolling everywhere (Home.css), which turns the jump back into a slow
 * scroll that stops short, especially while images are still loading. Here the
 * jump is instant, and it is repeated for a moment while the page finishes
 * laying out, unless the visitor starts scrolling themselves.
 *
 * `stayOnChange`: for pages whose address changes without the page itself
 * changing (the home page's /about and /contact panels). Moving between those
 * addresses keeps the current scroll position; each one is still remembered
 * for Back/Forward from other pages.
 */
const STORAGE_KEY = "scroll-positions";
const SETTLE_MS = 1200;

// The browser would otherwise put a reloaded page back where it was
if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";

// The first page shown after loading the site: only restore its position if
// the browser got here with Back/Forward (not a reload, typed address or link)
let firstPageStartsAtTop =
  performance.getEntriesByType?.("navigation")?.[0]?.type !== "back_forward";

const readPositions = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const savePosition = (key, y) => {
  try {
    const positions = readPositions();
    positions[key] = y;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Storage unavailable (e.g. private mode): just don't remember
  }
};

export default function useScrollMemory({ stayOnChange = false } = {}) {
  const { key } = useLocation();
  const navigationType = useNavigationType();
  const shownKey = useRef(null); // the address this page last settled on

  useLayoutEffect(() => {
    const root = document.documentElement;
    // Same page, new address (e.g. a panel opened): stay where we are
    if (stayOnChange && shownKey.current !== null && shownKey.current !== key) {
      shownKey.current = key;
      return () => savePosition(key, window.scrollY);
    }
    shownKey.current = key;
    // Back/Forward: where it was. New visits and reloads: the top.
    const startAtTop = firstPageStartsAtTop;
    firstPageStartsAtTop = false;
    const target = navigationType === "POP" && !startAtTop ? readPositions()[key] ?? 0 : 0;
    const started = performance.now();
    let frame = null;

    const stop = () => {
      cancelAnimationFrame(frame);
      root.style.scrollBehavior = "";
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", stop);
    };

    const jump = () => {
      window.scrollTo(0, target);
      if (performance.now() - started < SETTLE_MS) {
        frame = requestAnimationFrame(jump);
      } else {
        stop();
      }
    };

    root.style.scrollBehavior = "auto";
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("keydown", stop);
    jump();

    return () => {
      stop();
      // Runs before the page is removed, so this is still this page's position
      savePosition(key, window.scrollY);
    };
  }, [key, navigationType, stayOnChange]);
}
